import BaseRepository from "~/api/BaseRepository";
import type { $Fetch } from "nitropack";

export interface CreateExperienceData {
    name: string,
    description?: string,
    company_identifier: string,
    budget?: number,
    is_public?: boolean,
    mobility_type?: string,
    industry_id?: string,
    market_segment_ids?: string[],
    persona_ids?: string[],
    deal_size_ids?: string[],
    skill_ids?: string[],
    company_size_id?: string,
}

// Bulk create types for LinkedIn import
export interface BulkExperienceItem {
    title: string;
    company_name: string;
    description?: string;
    start_date?: string;  // YYYY-MM format
    end_date?: string;    // YYYY-MM format
    is_current?: boolean;
    location?: string;
    contract_type?: string;
    skill_ids?: string[]; // Skills per sub-period (from the split editor)
}

export interface BulkCreateExperiencesRequest {
    experiences: BulkExperienceItem[];
}

export interface BulkExperienceResult {
    experience_id?: string;
    title: string;
    company_name: string;
    success: boolean;
    error?: string;
}

export interface BulkCreateExperiencesResponse {
    created_count: number;
    failed_count: number;
    total_count: number;
    results: BulkExperienceResult[];
}

export interface ReferralRequest {
    experience_id: string,
    email: string,
}

export interface ReferralResponse {
    success: boolean,
    message: string,
}

export interface Experience {
    id: string;
}

export interface StatisticItem {
    id: string;
    name: string;
    percentage: number;
    count: number;
    validated_count: number;
}

export interface ExperienceStatisticsResponse {
    company_sizes: StatisticItem[];
    deal_sizes: StatisticItem[];
    market_segments: StatisticItem[];
    industries: StatisticItem[];
}

export const ExperienceRepository = (client: $Fetch) => ({
    ...BaseRepository(client)('v2/experiences'),

    sendReferral: (data: ReferralRequest): Promise<ReferralResponse> => {
        return client(`/v2/experiences/referral`, {
            method: 'POST',
            body: data
        });
    },
    update: (id: string | number, data, config = {}) => client(`/v2/experiences/${id}`, { method: 'PUT', body: data, ...config }),
    getStatistics: (userId?: string): Promise<ExperienceStatisticsResponse> => {
        const params = userId ? `?user_id=${userId}` : '';
        return client(`/v2/experiences/statistics${params}`, { method: 'GET' });
    },
    /**
     * Bulk create experiences from LinkedIn import
     * @param companyId - The freelance's company ID (required query param)
     * @param data - The bulk create request containing experiences array
     */
    bulkCreate: (companyId: string, data: BulkCreateExperiencesRequest): Promise<BulkCreateExperiencesResponse> => {
        return client(`/v2/experiences/bulk?company_id=${companyId}`, {
            method: 'POST',
            body: data
        });
    },
    /**
     * Split a declarative CDI experience into several sub-experiences.
     * Replaces the original experience atomically server-side.
     */
    split: (id: string, sub_experiences: { name: string; start_at: string; end_at: string | null; skill_ids: string[] }[]): Promise<{ created_ids: string[] }> => {
        return client(`/v2/experiences/${id}/split`, {
            method: 'POST',
            body: { sub_experiences },
        });
    },
    /**
     * Upload a company logo for a declarative experience.
     * Allowed only if the experience's company is a "shell" (no attached users) — enforced server-side.
     */
    uploadLogo: (experienceId: string, file: File): Promise<{ signed_logo_url: string | null }> => {
        const formData = new FormData();
        formData.append('file', file);
        return client(`/v2/experiences/${experienceId}/logo`, {
            method: 'POST',
            body: formData,
        });
    },
    /**
     * Submit a company logo correction request for an existing experience (admin-moderated).
     * Used in edit mode when the company is not eligible for direct upload
     * (`is_logo_editable === false`, e.g. it already has users/logo) but the freelance
     * wants to propose a fix. An admin reviews and approves/rejects the request.
     */
    submitLogoRequest: (experienceId: string, file: File): Promise<{ logo_change_request_id: string }> => {
        const formData = new FormData();
        formData.append('file', file);
        return client(`/v2/experiences/${experienceId}/logo-requests`, {
            method: 'POST',
            body: formData,
        });
    },
});